import { useState, useEffect, useRef } from "react";
import { MessageSquare, Send, Check, Loader } from "lucide-react";

export default function AnswerBox({
  prompt,
  placeholder = "Write your answer here...",
  onSave,
  savedAnswer,
  lessonId,
  childId,
  promptIndex,
  maxLength = 500
}) {
  const [answer, setAnswer] = useState(savedAnswer || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (savedAnswer) {
      setAnswer(savedAnswer);
      setSaved(true);
    }
  }, [savedAnswer]);

  const handleChange = (e) => {
    const value = e.target.value;
    if (value.length <= maxLength) {
      setAnswer(value);
      setSaved(false);
      
      // Auto-save after 2 seconds of no typing
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        if (value.trim()) {
          handleSave(value);
        }
      }, 2000);
    }
  };

  const handleSave = async (content = answer) => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await onSave?.(content, promptIndex);
      setSaved(true);
    } catch (e) {
      console.error("Failed to save answer:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl shadow-lg overflow-hidden mb-5">
      <div className="p-4 border-b" style={{ background: "linear-gradient(90deg, #118AB2, #06D6A0)" }}>
        <div className="flex items-center gap-2">
          <MessageSquare size={20} color="white" strokeWidth={2.5} />
          <h3 className="font-black text-white text-sm" style={{ fontFamily: "Fredoka, sans-serif" }}>
            Your Answer
          </h3>
        </div>
      </div>
      
      <div className="p-4">
        {prompt && (
          <p className="text-gray-700 font-semibold text-sm mb-3 leading-relaxed" 
            style={{ fontFamily: "Quicksand, sans-serif" }}>
            {prompt}
          </p>
        )}
        
        <textarea
          value={answer}
          onChange={handleChange}
          placeholder={placeholder}
          rows={4}
          className="w-full border-2 border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#118AB2] resize-none transition-colors"
          style={{ fontFamily: "Quicksand, sans-serif" }}
          data-testid={`answer-box-${promptIndex || 0}`}
        />
        
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-400">
            {answer.length}/{maxLength}
          </span>
          
          <div className="flex items-center gap-2">
            {saved && !saving && (
              <span className="flex items-center gap-1 text-xs text-green-600 font-bold">
                <Check size={14} /> Saved
              </span>
            )}
            
            <button
              onClick={() => handleSave()}
              disabled={saving || !answer.trim() || saved}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white font-bold text-sm disabled:opacity-50 transition-all"
              style={{ background: saved ? "#06D6A0" : "linear-gradient(90deg, #118AB2, #06D6A0)" }}
              data-testid={`save-answer-btn-${promptIndex || 0}`}
            >
              {saving ? (
                <Loader size={14} className="animate-spin" />
              ) : saved ? (
                <Check size={14} />
              ) : (
                <Send size={14} />
              )}
              {saving ? "Saving..." : saved ? "Saved!" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
